import React, { useState, useEffect } from 'react';
import './AdminCVList.scss';

interface CVData {
  id: number;
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address: string;
  birth_date: string;
  description: string;
  profile_image_url: string;
  work_experience: any[];
  education: any[];
  additional_training: any[];
  language_skills: any[];
  computer_skills: any;
  driving_skills: any[];
  personal_qualities: string;
  hobbies: string;
  organization_memberships: any[];
  recommendations: any[];
  job_preferences: any;
  cv_visibility_restriction: boolean;
  created_at: string;
  updated_at: string;
}

const AdminCVList: React.FC = () => {
  const [cvs, setCvs] = useState<CVData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCV, setSelectedCV] = useState<CVData | null>(null);

  useEffect(() => {
    fetchAllCVs();
  }, []);

  const fetchAllCVs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`/api/cv/admin/all`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        console.log('CV List API Response:', result);
        const cvsData = result.data || result;
        // Ensure we always have an array
        setCvs(Array.isArray(cvsData) ? cvsData : []);
      } else {
        const errorText = await response.text();
        console.error('CV List API Error:', response.status, errorText);
        setError('Nepavyko užkrauti CV sąrašo');
      }
    } catch (error) {
      console.error('Error fetching CVs:', error);
      setError('Klaida kraunant CV duomenis');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCV = async (userId: number) => {
    if (!window.confirm('Ar tikrai norite ištrinti šį CV?')) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const API_URL = '/api';
      const response = await fetch(`${API_URL}/cv/admin/user/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setCvs(cvs.filter(cv => cv.user_id !== userId));
        setSelectedCV(null);
      } else {
        setError('Nepavyko ištrinti CV');
      }
    } catch (error) {
      console.error('Error deleting CV:', error);
      setError('Klaida trinant CV');
    }
  };

  const filteredCVs = Array.isArray(cvs) ? cvs.filter(cv => 
    cv.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cv.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cv.email?.toLowerCase().includes(searchTerm.toLowerCase())
  ) : [];

  const handleViewCV = (cv: CVData) => {
    setSelectedCV(cv);
  };

  const closeModal = () => {
    setSelectedCV(null);
  };

  if (loading) {
    return (
      <div className="admin-cv-list">
        <div className="loading">Kraunama...</div>
      </div>
    );
  }

  return (
    <div className="admin-cv-list">
      <div className="page-header">
        <h1>CV valdymas</h1>
        <p>Peržiūrėkite ir valdykite visų darbuotojų CV</p>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="search-section">
        <input
          type="text"
          placeholder="Ieškoti pagal vardą, pavardę arba el. paštą..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="cvs-table">
        <table>
          <thead>
            <tr>
              <th>Vardas Pavardė</th>
              <th>El. paštas</th>
              <th>Telefonas</th>
              <th>Gyvenamoji vieta</th>
              <th>Sukurta</th>
              <th>Veiksmai</th>
            </tr>
          </thead>
          <tbody>
            {filteredCVs.map((cv) => (
              <tr key={cv.id}>
                <td>{cv.first_name} {cv.last_name}</td>
                <td>{cv.email}</td>
                <td>{cv.phone}</td>
                <td>{cv.address}</td>
                <td>{new Date(cv.created_at).toLocaleDateString('lt-LT')}</td>
                <td>
                  <div className="action-buttons">
                    <button 
                      onClick={() => handleViewCV(cv)}
                      className="btn btn-primary"
                    >
                      Peržiūrėti
                    </button>
                    <button 
                      onClick={() => handleDeleteCV(cv.user_id)}
                      className="btn btn-danger"
                    >
                      Ištrinti
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredCVs.length === 0 && !loading && (
          <div className="no-results">
            {searchTerm ? 'Nerasta CV pagal paieškos kriterijus' : 'Nėra sukurtų CV'}
          </div>
        )}
      </div>

      {/* CV Details Modal */}
      {selectedCV && (
        <div className="cv-modal-overlay" onClick={closeModal}>
          <div className="cv-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>CV: {selectedCV.first_name} {selectedCV.last_name}</h2>
              <button onClick={closeModal} className="close-btn">&times;</button>
            </div>
            
            <div className="modal-content">
              {/* Personal Information */}
              <div className="cv-section">
                <h3>Asmeninė informacija</h3>
                <div className="info-grid">
                  <div><strong>Vardas:</strong> {selectedCV.first_name || '-'}</div>
                  <div><strong>Pavardė:</strong> {selectedCV.last_name || '-'}</div>
                  <div><strong>El. paštas:</strong> {selectedCV.email || '-'}</div>
                  <div><strong>Telefonas:</strong> {selectedCV.phone || '-'}</div>
                  <div><strong>Adresas:</strong> {selectedCV.address || '-'}</div>
                  <div><strong>Gimimo data:</strong> {selectedCV.birth_date || '-'}</div>
                </div>
              </div>

              {/* Professional Description */}
              {selectedCV.description && (
                <div className="cv-section">
                  <h3>Profesinis aprašymas</h3>
                  <p>{selectedCV.description}</p>
                </div>
              )}

              {/* Work Experience */}
              {selectedCV.work_experience && Array.isArray(selectedCV.work_experience) && selectedCV.work_experience.length > 0 && (
                <div className="cv-section">
                  <h3>Darbo patirtis</h3>
                  {selectedCV.work_experience.map((exp: any, index: number) => (
                    <div key={index} className="experience-item">
                      <div className="experience-header">
                        <strong>{exp.position || 'Nenurodyta pareigos'}</strong>
                        {exp.companyName && <span> - {exp.companyName}</span>}
                      </div>
                      <div className="experience-details">
                        {(exp.startDate || exp.endDate) && <div><strong>Laikotarpis:</strong> {exp.startDate || '?'} – {exp.endDate || 'dabar'}</div>}
                        {exp.workField && <div><strong>Darbo sritis:</strong> {exp.workField}</div>}
                        {exp.companyField && <div><strong>Įmonės veiklos sritis:</strong> {exp.companyField}</div>}
                        {exp.description && <div><strong>Darbo pobūdis:</strong> {exp.description}</div>}
                        {exp.achievements && <div><strong>Pasiekimai:</strong> {exp.achievements}</div>}
                        {exp.companyWebsite && <div><strong>Įmonės svetainė:</strong> {exp.companyWebsite}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Education */}
              {selectedCV.education && Array.isArray(selectedCV.education) && selectedCV.education.length > 0 && (
                <div className="cv-section">
                  <h3>Išsilavinimas</h3>
                  {selectedCV.education.map((edu: any, index: number) => (
                    <div key={index} className="education-item">
                      <div className="education-header">
                        <strong>{edu.institution || 'Nenurodyta įstaiga'}</strong>
                      </div>
                      <div className="education-details">
                        {(edu.startDate || edu.endDate) && <div><strong>Laikotarpis:</strong> {edu.startDate || '?'} – {edu.endDate || 'dabar'}</div>}
                        {edu.educationType && <div><strong>Išsilavinimo tipas:</strong> {edu.educationType}</div>}
                        {edu.degree && <div><strong>Laipsnis:</strong> {edu.degree}</div>}
                        {edu.specialization && <div><strong>Specialybė:</strong> {edu.specialization}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Additional Training */}
              {selectedCV.additional_training && Array.isArray(selectedCV.additional_training) && selectedCV.additional_training.length > 0 && (
                <div className="cv-section">
                  <h3>Papildomi mokymai</h3>
                  {selectedCV.additional_training.map((training: any, index: number) => (
                    <div key={index} className="training-item">
                      <div className="training-header">
                        <strong>{training.courseName || 'Nenurodytas kursas'}</strong>
                      </div>
                      <div className="training-details">
                        {training.year && <div><strong>Metai:</strong> {training.year}</div>}
                        {training.duration && <div><strong>Trukmė:</strong> {training.duration}</div>}
                        {training.provider && <div><strong>Teikėjas:</strong> {training.provider}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Language Skills */}
              {selectedCV.language_skills && Array.isArray(selectedCV.language_skills) && selectedCV.language_skills.length > 0 && (
                <div className="cv-section">
                  <h3>Kalbų įgūdžiai</h3>
                  {selectedCV.language_skills.map((lang: any, index: number) => (
                    <div key={index} className="language-item">
                      <div className="language-header">
                        <strong>{lang.language || 'Nenurodyta kalba'}</strong>
                      </div>
                      <div className="language-details">
                        {lang.understanding && <div><strong>Supratimas:</strong> {lang.understanding}/5</div>}
                        {lang.speaking && <div><strong>Kalbėjimas:</strong> {lang.speaking}/5</div>}
                        {lang.writing && <div><strong>Rašymas:</strong> {lang.writing}/5</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Computer Skills — each category is a list of { name, level } objects. */}
              {selectedCV.computer_skills && (() => {
                const skillNames = (arr: any): string => (Array.isArray(arr)
                  ? arr.map((s: any) => (typeof s === 'string' ? s : (s?.name || ''))).filter(Boolean).join(', ')
                  : '');
                const cats: Array<[string, string]> = [
                  ['office', 'Biuro programos'],
                  ['accounting', 'Apskaitos programos'],
                  ['programming', 'Programavimo kalbos'],
                  ['os', 'Operacinės sistemos'],
                  ['database', 'Duomenų bazės'],
                  ['design', 'Dizaino programos'],
                ];
                const rows = cats
                  .map(([key, label]) => [label, skillNames(selectedCV.computer_skills[key])] as [string, string])
                  .filter(([, names]) => names);
                if (rows.length === 0) return null;
                return (
                  <div className="cv-section">
                    <h3>Kompiuterių įgūdžiai</h3>
                    <div className="computer-skills">
                      {rows.map(([label, names]) => (
                        <div key={label}><strong>{label}:</strong> {names}</div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Driving Skills */}
              {selectedCV.driving_skills && Array.isArray(selectedCV.driving_skills) && selectedCV.driving_skills.length > 0 && (
                <div className="cv-section">
                  <h3>Vairavimo įgūdžiai</h3>
                  {selectedCV.driving_skills.map((driving: any, index: number) => (
                    <div key={index} className="driving-item">
                      <div><strong>Kategorija:</strong> {driving.category || '-'}</div>
                      <div><strong>Patirtis:</strong> {driving.experience || '-'}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Personal Qualities */}
              {selectedCV.personal_qualities && (
                <div className="cv-section">
                  <h3>Asmeninės savybės</h3>
                  <p>{selectedCV.personal_qualities}</p>
                </div>
              )}

              {/* Hobbies */}
              {selectedCV.hobbies && (
                <div className="cv-section">
                  <h3>Pomėgiai, hobiai</h3>
                  <p>{selectedCV.hobbies}</p>
                </div>
              )}

              {/* Organization Memberships */}
              {selectedCV.organization_memberships && Array.isArray(selectedCV.organization_memberships) && selectedCV.organization_memberships.length > 0 && (
                <div className="cv-section">
                  <h3>Narystė organizacijose</h3>
                  {selectedCV.organization_memberships.map((org: any, index: number) => (
                    <div key={index} className="organization-item">
                      <div><strong>Pavadinimas:</strong> {org.name || '-'}</div>
                      <div><strong>Statusas:</strong> {org.status || '-'}</div>
                      {(org.startDate || org.endDate) && <div><strong>Laikotarpis:</strong> {org.startDate || '?'} – {org.endDate || 'dabar'}</div>}
                    </div>
                  ))}
                </div>
              )}

              {/* Recommendations */}
              {selectedCV.recommendations && Array.isArray(selectedCV.recommendations) && selectedCV.recommendations.length > 0 && (
                <div className="cv-section">
                  <h3>Rekomendacijos</h3>
                  {selectedCV.recommendations.map((rec: any, index: number) => (
                    <div key={index} className="recommendation-item">
                      <div><strong>Asmens vardas:</strong> {rec.personName || rec.name || '-'}</div>
                      <div><strong>Įmonė:</strong> {rec.company || '-'}</div>
                      <div><strong>Pareigos:</strong> {rec.position || '-'}</div>
                      <div><strong>Telefonas:</strong> {rec.phone || '-'}</div>
                      <div><strong>El. paštas:</strong> {rec.email || '-'}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Job Preferences — values may be arrays (per-item) or scalars. */}
              {selectedCV.job_preferences && (() => {
                const jp = selectedCV.job_preferences;
                const fmt = (v: any): string => (Array.isArray(v) ? v.filter(Boolean).join(', ') : (v ?? '').toString());
                const fromItems = (key: string) =>
                  Array.isArray(jp.items) ? jp.items.flatMap((item: any) => {
                    const val = item?.[key];
                    return Array.isArray(val) ? val : (val ? [val] : []);
                  }).filter(Boolean).join(', ') : '';
                const pick = (key: string, fallback?: any) => fmt(jp[key] ?? fallback) || fromItems(key);
                const rows: Array<[string, string]> = [
                  ['Pageidaujamas miestas', pick('workLocation', jp.location)],
                  ['Darbo tipas', pick('workType')],
                  ['Darbo laikas', pick('workTime')],
                  ['Pareigos', pick('position')],
                  ['Atlyginimas (nuo)', pick('salaryMin', jp.salary) ? `${pick('salaryMin', jp.salary)} EUR` : ''],
                ].filter(([, v]) => v) as Array<[string, string]>;
                if (rows.length === 0) return null;
                return (
                  <div className="cv-section">
                    <h3>Pageidaujamas darbas</h3>
                    <div className="info-grid">
                      {rows.map(([label, v]) => <div key={label}><strong>{label}:</strong> {v}</div>)}
                    </div>
                  </div>
                );
              })()}

              {/* CV Visibility */}
              <div className="cv-section">
                <h3>CV nustatymai</h3>
                <div className="info-grid">
                  <div><strong>CV matomumas:</strong> {selectedCV.cv_visibility_restriction ? 'Ribojamas' : 'Viešas'}</div>
                  <div><strong>Sukurta:</strong> {new Date(selectedCV.created_at).toLocaleString('lt-LT')}</div>
                  <div><strong>Atnaujinta:</strong> {new Date(selectedCV.updated_at).toLocaleString('lt-LT')}</div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button onClick={closeModal} className="btn btn-secondary">
                Uždaryti
              </button>
              <button 
                onClick={() => handleDeleteCV(selectedCV.user_id)}
                className="btn btn-danger"
              >
                Ištrinti CV
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCVList;
